import { Header, MenuConfig, ModalRef } from 'alurkerja-ui';
import { RefObject, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import { Notifications } from '@/utils/types/user';
import { useFetch } from '@/hooks/useFetch';
import { TimeAgo } from '@/components/shared/timeAgo/TimeAgo';
import { ExtendedSidebar } from '@/components/shared/extendedSidebar/ExtendedSidebar';
import { axiosInstance } from '@/utils/lib/axios';
import { useKeycloak } from 'keycloak-react-web';

const MenuWrapper = (props: { children: JSX.Element; menu: MenuConfig }) => {
  const { children, menu } = props;
  return <Link to={menu.href}>{children}</Link>;
};

export const AdminLayout = () => {
  const ref = useRef<ModalRef>(null);
  const { keycloak } = useKeycloak();
  const name = keycloak.tokenParsed?.given_name;
  const [toggled, setToggled] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const { data: notifications, refetch } = useFetch<Notifications>({
    path: '/api/notifications?limit=100&sort=createdDate&asc=false',
  });

  useEffect(() => {
    if (notifications) {
      const unreadNotifications = notifications.content.filter(notification => notification.read_at === null);
      setNotificationCount(unreadNotifications.length);
    }
  }, [notifications]);

  const AvatarContent = () => (
    <div className="p-4">
      <button
        className="w-full p-2 text-left rounded cursor-pointer hover:bg-gray-100 "
        onClick={() => keycloak.logout()}
      >
        Keluar
      </button>
    </div>
  );

  return (
    <div className="max-w-screen ">
      <ExtendedSidebar toggled={toggled} setToggled={setToggled} menuWrapper={MenuWrapper} />

      <div
        className={twJoin(
          'transition-[margin] ease-in-out duration-400',
          toggled ? 'ml-[80px]' : 'ml-[80px] md:ml-[270px]',
        )}
      >
        <div className="sticky top-0 z-10">
          <Header
            avatarContent={<AvatarContent />}
            role={name}
            notificationContent={<NotificationContent refetch={refetch} notifications={notifications} ref={ref} />}
            notificationCount={notificationCount}
          />
        </div>

        <main className="px-0 py-0 md:py-8 md:px-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

function NotificationContent({
  refetch,
  notifications,
  ref,
}: {
  refetch: () => void;
  notifications?: Notifications;
  ref: RefObject<ModalRef>;
}) {
  const navigate = useNavigate();

  const markAllAsRead = async () => {
    try {
      await axiosInstance.post(`/api/notifications/read-all`);
      refetch();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };
  return (
    <div className="p-3 -ml-40 bg-white rounded-xl w-96 shadow-xl">
      <div className="m-3 text-xs text-end">
        <Link className="text-blue-700 text-end" to={'#'} onClick={() => markAllAsRead()}>
          Tandai Semua Sudah Dibaca
        </Link>
      </div>
      <div className="flex-col mx-3 mb-3">
        {notifications?.content ? (
          notifications?.content.slice(0, 3).map(notification => (
            <button
              key={notification.id}
              className="text-left"
              onClick={() => {
                navigate(notification.url);
                ref.current?.closeModal();
              }}
            >
              <div className="relative w-full max-w-lg p-4 mb-1 border border-gray-300 rounded-md">
                {notification.read_at === null && (
                  <span className="absolute top-0 right-0 mt-2 mr-2 text-4xl text-red-500">•</span>
                )}
                <p className="text-lg font-semibold">{notification.title}</p>
                <p className="mt-2 text-sm">{notification.message}</p>
                <TimeAgo timestamp={notification.created_at} />
              </div>
            </button>
          ))
        ) : (
          <div className="py-6">
            <p className="text-center text-sm text-slate-500">Tidak ada notifikasi</p>
          </div>
        )}
      </div>
      <div className="text-center text-md">
        <Link to="/notifications" className="text-blue-700 text-end">
          Lihat Semua Notifikasi
        </Link>
      </div>
    </div>
  );
}
